import {
  SingletonService,
  CustomElementsUtil,
} from '@trp-dms-digital-shared/ds-ui-library-common';

export const FOOTNOTE_INDICATOR_TYPES = {
  NUMBER: 'number',
  SYMBOL: 'symbol',
} as const;

/**
 * The `FootnoteIndicatorService` class provides functionality for applying footnotes to disclaimer containers in the document body.
 * It manages the generation and application of footnote indicators as numbers and symbols.
 */
@SingletonService
export class FootnoteIndicatorService {
  private readonly symbols: string[] = ['*', '†', '‡', '#'];
  private symbolIndex = 0;
  private numberIndex = 1;

  private readonly footnoteIndicatorMap: Map<string, string> = new Map<
    string,
    string
  >();

  private readonly disclaimerContainerSelectors = ['pop-disclosure-component'];
  private readonly DATA_DISCLAIMER_CODE = 'data-disclaimer-code';
  private readonly DATA_FOOTNOTE_INDICATOR_TYPE =
    'data-footnote-indicator-type';

  constructor(_disclaimerContainerSelectors?: string[]) {
    if (_disclaimerContainerSelectors) {
      this.disclaimerContainerSelectors = [
        ..._disclaimerContainerSelectors,
        ...this.disclaimerContainerSelectors,
      ];
    }
  }

  /**
   * Applies footnote indicators to all disclaimer containers in the document body.
   */
  public applyFootnoteIndicatorsToAll() {
    this.resetFootnoteIndicatorNumberCounters();

    const shadowDomElements: Element[] = [];
    CustomElementsUtil.queryNodesinShadowRoot(
      document?.body,
      this.disclaimerContainerSelectors.join(','),
      '[pop-version]',
      shadowDomElements,
    );
    let disclaimerContainers = [...shadowDomElements];
    const containers = document?.querySelectorAll(
      this.disclaimerContainerSelectors.join(','),
    );
    if (containers) {
      disclaimerContainers = [...containers, ...shadowDomElements];
    }
    disclaimerContainers.forEach((disclaimerContainer) => {
      const disclaimerContainerElements =
        this.findDisclaimerElements(disclaimerContainer);

      this.applyFootnoteIndicatorToDisclaimerElements(
        disclaimerContainerElements,
        this.getFootnoteIndicatorType(disclaimerContainer),
      );
    });
  }

  /**
   * Retrieves the footnote indicator type for the given element.
   * If the element does not have a `data-footnote-indicator-type` attribute,
   * it returns the default footnote type `FOOTNOTE_INDICATOR_TYPES.NUMBER`.
   *
   * @param element - The element for which to retrieve the footnote indicator type.
   * @returns The footnote indicator type of the element.
   */
  getFootnoteIndicatorType(element: Element) {
    return (
      element.getAttribute(this.DATA_FOOTNOTE_INDICATOR_TYPE) ||
      FOOTNOTE_INDICATOR_TYPES.NUMBER
    );
  }

  /**
   * Reset needed for tests
   */
  public resetFootnoteIndicatorCounters() {
    this.symbolIndex = 0;
    this.numberIndex = 1;
    this.footnoteIndicatorMap.clear();
  }

  /**
   * Reset only the number index and clear the map of any number indicators
   */
  public resetFootnoteIndicatorNumberCounters() {
    this.numberIndex = 1;
    this.footnoteIndicatorMap.forEach((value, key) => {
      if (!isNaN(Number(value))) {
        this.footnoteIndicatorMap.delete(key);
      }
    });
  }

  /**
   * Gets the next available footnote indicator number.
   *
   * @returns The next available footnote indicator number.
   */
  getFootnoteIndicatorNumber() {
    const footnoteIndicatorNumber = this.numberIndex;
    this.numberIndex++;

    return footnoteIndicatorNumber;
  }

  /**
   * Returns the footnote indicator symbol based on the current symbol index.
   * @returns The footnote indicator symbol.
   */
  getFootnoteIndicatorSymbol() {
    const numberOfSymbols =
      Math.floor(this.symbolIndex / this.symbols.length) + 1;

    const symbol = this.symbols[this.symbolIndex % this.symbols.length];

    this.symbolIndex++;

    return `${symbol.repeat(numberOfSymbols)}`;
  }

  /**
   * Finds elements in the given root element that match the specified selector.
   * This method searches both the light DOM and shadow DOM elements.
   *
   * @param root - The root element to search within.
   * @param selector - The CSS selector to match elements against.
   * @returns An array of elements that match the selector.
   */
  findElementsBySelector(root: Element, selector: string) {
    const lightDomElements = root.querySelectorAll(selector);

    const shadowDomElements: Element[] = [];
    CustomElementsUtil.queryNodesinShadowRoot(
      root,
      selector,
      '[pop-version]',
      shadowDomElements,
    );

    return [...lightDomElements, ...shadowDomElements];
  }

  /**
   * Finds disclaimer elements within the given root element.
   * @param root - The root element to search within.
   * @returns An array of footnote elements.
   */
  findDisclaimerElements(root: Element) {
    return this.findElementsBySelector(root, `[${this.DATA_DISCLAIMER_CODE}]`);
  }

  /**
   * Finds the matching disclaimer elements in the given root element based on the provided code.
   * @param root - The root element to search within.
   * @param code - The code to match against the data disclaimer code attribute.
   * @param empty - Whether to only return elements that are empty.
   * @returns An array of matching footnote elements.
   */
  findMatchingDisclaimerElementsByCode(
    root: Element,
    code: string,
    empty = true,
  ) {
    return this.findElementsBySelector(
      root,
      `[${this.DATA_DISCLAIMER_CODE}="${code}"]${empty ? ':empty' : ''}`,
    );
  }

  /**
   * Retrieves the footnote indicator for the given element and footnote indicator type.
   * @param element - The element for which to retrieve the footnote indicator.
   * @param footnoteType - The type of footnote indicator to retrieve.
   * @returns The footnote indicator string.
   */
  getFootnoteIndicator(element: Element, footnoteType: string) {
    const disclaimerCode =
      element.getAttribute(this.DATA_DISCLAIMER_CODE) || '';
    if (this.footnoteIndicatorMap.has(disclaimerCode)) {
      return this.footnoteIndicatorMap.get(disclaimerCode);
    }

    let footnoteIndicator = '';
    if (footnoteType === FOOTNOTE_INDICATOR_TYPES.NUMBER) {
      footnoteIndicator = `${this.getFootnoteIndicatorNumber()}`;
    } else if (footnoteType === FOOTNOTE_INDICATOR_TYPES.SYMBOL) {
      footnoteIndicator = this.getFootnoteIndicatorSymbol();
    }

    this.footnoteIndicatorMap.set(disclaimerCode, footnoteIndicator);

    return footnoteIndicator;
  }

  /**
   * Renders the footnote indicator type for an element and its matching elements.
   *
   * @param disclaimerElement - The element to set the footnote indicator type for.
   * @param matchingDisclaimerElements - An array of matching elements to also set the footnote indicator type for.
   * @param footnoteIndicatorType - The type of footnote indicator to set.
   */
  renderFootnoteIndicator(
    disclaimerElement: Element,
    matchingDisclaimerElements: Element[],
    footnoteIndicatorType: string,
  ) {
    const footnoteIndicator = this.getFootnoteIndicator(
      disclaimerElement,
      footnoteIndicatorType,
    );

    disclaimerElement.innerHTML = `<sup>${footnoteIndicator}</sup>`;

    matchingDisclaimerElements.forEach((matchingDisclaimerElement) => {
      matchingDisclaimerElement.innerHTML = `<sup>${footnoteIndicator}</sup>`;
    });
  }

  /**
   * Applies a footnote indicator to an array of elements.
   *
   * @param disclaimerElements - The elements to apply the footnote indicator to.
   * @param footnoteIndicatorType - The type of footnote indicator to apply (default: 'number').
   */
  applyFootnoteIndicatorToDisclaimerElements(
    disclaimerElements: Element[],
    footnoteIndicatorType = 'number',
  ) {
    disclaimerElements.forEach((disclaimerElement) => {
      let matchingDisclaimerElements =
        this.findMatchingDisclaimerElementsByCode(
          document?.body,
          disclaimerElement.getAttribute(this.DATA_DISCLAIMER_CODE) || '',
          footnoteIndicatorType === FOOTNOTE_INDICATOR_TYPES.SYMBOL,
        );

      matchingDisclaimerElements = matchingDisclaimerElements?.filter(
        (matchingDisclaimerElement) =>
          matchingDisclaimerElement !== disclaimerElement,
      );

      if (matchingDisclaimerElements.length === 0) {
        return;
      }

      this.renderFootnoteIndicator(
        disclaimerElement,
        matchingDisclaimerElements,
        footnoteIndicatorType,
      );
    });
  }

  /**
   * This method is used to get Indicator symbol for given disclaimer content
   * @param disclaimerContent - Its html encoded string that contains disclaimer content (ex: sample text <span data-disclaimer-code="disclaimerCode"><span>
   */
  getIndicatorSymbol(disclaimerCode: string) {
    if (this.footnoteIndicatorMap.has(disclaimerCode)) {
      return `<sup>${this.footnoteIndicatorMap.get(disclaimerCode)}</sup>`;
    }
    return '';
  }
}
